import captureError from '@disco/utils/captureError'
import uniqBy from 'lodash/uniqBy'
import NavigationService from '@disco/state-management/NavigationService'
import type { CopyMetadataFields } from 'types/copy-metadata'
import type {
  Discussion,
  DiscussionVisibility,
  ListDiscussionsParams,
} from 'types/discussion'
import type { Thunk } from 'types/redux'
import type {
  RightsSplitChain,
  RightsSplitChainRequest,
  RightsType,
} from 'types/rights-data'
import type { NativeTag, Track, TrackId } from 'types/track'
import api, { BulkUpdateOptions } from '@disco/api/server'
import { SET_COPY_METADATA, TRACKS_UPDATE } from '@disco/constants/ActionTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import type { CopyMetadataState } from '@disco/state-management/reducers/copy-metadata'
import { discussionsApi } from '@disco/state-management/api/discussions'
import type { GetTrackRightsSplitsParams } from '@disco/state-management/api/rights-splits'
import { rightsSplitsApi } from '@disco/state-management/api/rights-splits'
import { loadTrackRightsFromRightsApi } from '@disco/state-management/reducers/rightsManagement'
import {
  selectCopyMetadataSourceTrack,
  selectCopyMetadataState,
} from '@disco/state-management/selectors/copy-metadata'
import { pick } from '@disco/utils/array'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'
import { plural } from '@disco/utils/string-utils'

type RtkqPatch = { undo: () => void }
const REQUEST_DELAY = 10
const RIGHTS_TYPES: RightsType[] = ['publishing', 'recording']

/**
 * Helper function to extract native tag IDs from a tags array.
 * @param tags - Array of native tags or null/undefined
 * @returns Array of native tag IDs
 */
function getNativeTagsIds(
  tags: NativeTag[] | null | undefined,
): NativeTag['id'][] {
  if (tags) {
    return tags.map((tag: NativeTag) => tag.id)
  }

  return []
}

/**
 * Sets the source and target tracks for metadata copying operation.
 * @param options - Configuration for copy metadata operation
 * @param options.sourceTrackId - ID of the track to copy metadata from
 * @param options.targetTrackIds - Array of track IDs to copy metadata to
 * @returns Action object to set copy metadata state
 */
export function setCopyMetadata({
  sourceTrackId,
  targetTrackIds,
  returnPath,
}: Partial<CopyMetadataState>): CopyMetadataState & {
  type: string
} {
  return {
    type: SET_COPY_METADATA,
    sourceTrackId,
    targetTrackIds: targetTrackIds ?? [],
    returnPath,
  }
}
/**
 * Copies selected metadata fields from source track to target tracks.
 * @param fields - Configuration specifying which metadata fields to copy
 * @param fields.artwork - Whether to copy artwork
 * @param fields.note_ids - Selected discussion ids to copy
 * @param fields.metadata_keys - Metadata keys to copy
 * @param fields.native_tags_indexes - Indexes of native tags to copy
 * @param fields.rights_split_ids - Selected rights split chain ids to copy
 * @param options.recordingTabEnabled - Whether the recording rights tab is
 *   surfaced for the current user. When false, recording chain queries and
 *   mutations are skipped entirely.
 * @returns Thunk that performs metadata copying with undo functionality
 */
export function copyMetadata(
  {
    artwork,
    note_ids,
    metadata_keys,
    native_tags_indexes,
    rights_split_ids,
  }: CopyMetadataFields,
  { recordingTabEnabled = false }: { recordingTabEnabled?: boolean } = {},
): Thunk {
  return async (dispatch, getState) => {
    const state = getState()
    const { sourceTrackId, targetTrackIds, returnPath } =
      selectCopyMetadataState(state)
    const sourceTrack: Track | null | undefined =
      selectCopyMetadataSourceTrack(state)

    if (!sourceTrack) {
      return
    }

    const newMetadata = pick(
      sourceTrack.metadata,
      Object.keys(metadata_keys).filter(k => metadata_keys[k]),
    )

    const native_tags = Object.keys(native_tags_indexes)
      .map((key: string) => {
        const index = Number(key)

        if (native_tags_indexes[key] && sourceTrack.native_tags) {
          return sourceTrack.native_tags[index]
        }

        return null
      })
      .filter(tag => tag !== null && tag !== undefined)

    const previousState: Partial<Track>[] = []
    const futureState: Partial<Track>[] = []
    const rightsPatches: RtkqPatch[] = []
    const discussionPatches: RtkqPatch[] = []
    const rightsPatchedTargetIds = new Set<TrackId>()
    const undoRtkqPatches = (patches: RtkqPatch[]) => {
      while (patches.length > 0) {
        patches.pop()?.undo()
      }
    }

    // sync with rights-management Redux slice
    const syncRightsSlice = (targetIds: Iterable<TrackId>) => {
      const latest = getState()
      for (const targetId of targetIds) {
        if (!latest.rightsManagement.splitsByTrack[targetId]) {
          continue
        }

        const cacheResult =
          rightsSplitsApi.endpoints.getTrackRightsSplits.select({
            trackId: targetId as number,
          })(latest)
        if (cacheResult.data === undefined) {
          continue
        }

        dispatch(
          loadTrackRightsFromRightsApi({
            trackId: targetId,
            rightsSplitChains: cacheResult.data,
          }),
        )
      }
    }

    const handleError = error => {
      dispatch({
        type: TRACKS_UPDATE,
        tracks: previousState,
      })
      undoRtkqPatches(rightsPatches)
      syncRightsSlice(rightsPatchedTargetIds)
      undoRtkqPatches(discussionPatches)
      pushUINotification({
        dismissible: 'none',
        level: 'error',
        message:
          'There was an error trying to copy track info. Please try again or contact us.',
        action: {
          label: 'Retry',
          callback: () => {
            dispatch(
              setCopyMetadata({
                sourceTrackId,
                targetTrackIds,
                returnPath,
              }),
            )
            NavigationService.push('/explorer/copy-metadata')
          },
        },
      })
      captureError(error, { extra: { level: 'info' } })
    }

    const update = {
      track_id_for_artwork:
        artwork && sourceTrack.artwork ? sourceTrackId : undefined,
      metadata: newMetadata,
      tags: getNativeTagsIds(native_tags),
    } as BulkUpdateOptions

    for (const id of targetTrackIds) {
      const track: Track = state.tracks[id]
      const type = getFileTypeByExt(track.ext)
      let previousArtwork = {}
      let futureArtwork = {}

      if (
        artwork &&
        sourceTrack.artwork &&
        [AUDIO_FILE, VIDEO_FILE].includes(type)
      ) {
        // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
        previousArtwork = pick(track, ['artwork', 'artwork_sizes'])
        futureArtwork = pick(sourceTrack, ['artwork', 'artwork_sizes'])
      }

      // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
      const oldAttrs = pick(track, ['name', 'album', 'artist'])
      const newAttrs: Partial<Track> = {}
      if ('Title' in newMetadata) newAttrs.name = newMetadata.Title
      if ('Album' in newMetadata) newAttrs.album = newMetadata.Album
      if ('Artist' in newMetadata) newAttrs.artist = newMetadata.Artist
      previousState.push({
        ...previousArtwork,
        id: track.id,
        ...oldAttrs,
        metadata: track.metadata,
        native_tags: track.native_tags,
      })
      futureState.push({
        ...futureArtwork,
        id: track.id,
        metadata: { ...track.metadata, ...newMetadata },
        ...newAttrs,
        native_tags: uniqBy(
          [...(track.native_tags ?? []), ...native_tags],
          'id',
        ),
      })
    }

    // Discussions - compute selection + apply optimistic patches
    const noteIdsToCopy = Object.keys(note_ids ?? {})
      .filter(key => note_ids[key])
      .map(Number)
    const selectedNoteIds = new Set(noteIdsToCopy)

    if (noteIdsToCopy.length > 0 && targetTrackIds.length > 0) {
      const filterDiscussions = (
        discussions: Discussion[],
        targetId: TrackId,
      ): Discussion[] =>
        discussions
          .filter(d => selectedNoteIds.has(Number(d.id)))
          .map(d => ({
            ...d,
            target_object_id: targetId as number,
            replies: (d.replies ?? []).filter(r =>
              selectedNoteIds.has(Number(r.id)),
            ),
          }))

      const sourceListEntries = discussionsApi.util.selectInvalidatedBy(state, [
        { type: 'Discussions', id: `LIST-track-${sourceTrackId}` },
      ])
      const sourceDiscussionsByVisibility: Partial<
        Record<DiscussionVisibility, Discussion[]>
      > = {}

      for (const { originalArgs } of sourceListEntries) {
        const args = originalArgs as ListDiscussionsParams
        if (!args.visibility) {
          continue
        }

        const data =
          discussionsApi.endpoints.getDiscussions.select(args)(state).data
        if (!data) {
          continue
        }

        sourceDiscussionsByVisibility[args.visibility] = data.results
      }

      for (const targetId of targetTrackIds) {
        const targetEntries = discussionsApi.util.selectInvalidatedBy(state, [
          { type: 'Discussions', id: `LIST-track-${targetId}` },
        ])
        for (const { originalArgs } of targetEntries) {
          const args = originalArgs as ListDiscussionsParams
          if (!args.visibility) {
            continue
          }

          const sourceDiscussions =
            sourceDiscussionsByVisibility[args.visibility] ?? []
          const toPrepend = filterDiscussions(sourceDiscussions, targetId)

          if (toPrepend.length === 0) {
            continue
          }

          discussionPatches.push(
            dispatch(
              discussionsApi.util.updateQueryData(
                'getDiscussions',
                args,
                draft => {
                  draft.results = [...toPrepend, ...draft.results]
                },
              ),
            ),
          )
        }
      }
    }

    const copyNotes = async () => {
      if (noteIdsToCopy.length === 0 || targetTrackIds.length === 0) {
        return
      }

      try {
        await dispatch(
          discussionsApi.endpoints.copyDiscussions.initiate({
            source: {
              target_type: 'track',
              target_object_id: sourceTrackId,
            },
            ids: noteIdsToCopy,
            targets: targetTrackIds.map(id => ({
              target_type: 'track',
              target_object_id: id,
            })),
          }),
        ).unwrap()
      } catch (error) {
        undoRtkqPatches(discussionPatches)
        pushUINotification({
          dismissible: 'none',
          level: 'error',
          message:
            'Track info was copied, but notes failed to copy. Please try again.',
        })
        captureError(error, { extra: { level: 'info' } })
      }
    }

    // Rights - compute selection + apply optimistic patches
    const selectedChainIds = new Set(
      Object.keys(rights_split_ids ?? {})
        .filter(key => rights_split_ids[key])
        .map(Number),
    )

    const toChainRequest = (
      chain: RightsSplitChain,
    ): Omit<RightsSplitChainRequest, 'rights_type'> => ({
      rights_percentage: chain.rights_percentage,
      territory: chain.territory,
      members: chain.members.map(member => ({
        entity_id: member.entity.id,
        role_type: member.role_type,
      })),
    })

    const enabledRightsTypes: RightsType[] = recordingTabEnabled
      ? RIGHTS_TYPES
      : RIGHTS_TYPES.filter(t => t !== 'recording')

    const selectedSourceChainsByType: Record<RightsType, RightsSplitChain[]> = {
      publishing: [],
      recording: [],
    }
    for (const rightsType of enabledRightsTypes) {
      const cached =
        rightsSplitsApi.endpoints.getTrackRightsSplits.select({
          trackId: sourceTrackId as number,
          rightsType,
        })(state).data ?? []
      selectedSourceChainsByType[rightsType] = cached.filter(c =>
        selectedChainIds.has(c.id),
      )
    }
    const hasAnySelectedSourceChains = enabledRightsTypes.some(
      rt => selectedSourceChainsByType[rt].length > 0,
    )

    if (
      selectedChainIds.size > 0 &&
      targetTrackIds.length > 0 &&
      hasAnySelectedSourceChains
    ) {
      const overwrittenTypes = new Set(
        enabledRightsTypes.filter(
          rt => selectedSourceChainsByType[rt].length > 0,
        ),
      )

      for (const targetId of targetTrackIds) {
        const targetEntries = rightsSplitsApi.util.selectInvalidatedBy(state, [
          ...enabledRightsTypes.map(rt => ({
            type: 'RightsSplits' as const,
            id: `TRACK-${targetId}-${rt}`,
          })),
          { type: 'RightsSplits', id: `TRACK-${targetId}-all` },
        ])
        for (const { originalArgs } of targetEntries) {
          const args = originalArgs as GetTrackRightsSplitsParams
          const remapForTarget = (c: RightsSplitChain) => ({
            ...c,
            track: targetId as number,
          })

          if (args.rightsType) {
            if (!overwrittenTypes.has(args.rightsType)) {
              continue
            }

            const next =
              selectedSourceChainsByType[args.rightsType].map(remapForTarget)
            rightsPatches.push(
              dispatch(
                rightsSplitsApi.util.updateQueryData(
                  'getTrackRightsSplits',
                  args,
                  () => next,
                ),
              ),
            )
            rightsPatchedTargetIds.add(targetId)
          } else {
            rightsPatches.push(
              dispatch(
                rightsSplitsApi.util.updateQueryData(
                  'getTrackRightsSplits',
                  args,
                  draft => {
                    const preserved = draft.filter(
                      c => !overwrittenTypes.has(c.rights_type),
                    )
                    const replacements = enabledRightsTypes.flatMap(rt =>
                      overwrittenTypes.has(rt)
                        ? selectedSourceChainsByType[rt].map(remapForTarget)
                        : [],
                    )
                    return [...preserved, ...replacements]
                  },
                ),
              ),
            )
            rightsPatchedTargetIds.add(targetId)
          }
        }
      }
    }

    const copyRightsSplits = async () => {
      if (selectedChainIds.size === 0 || targetTrackIds.length === 0) {
        return
      }
      try {
        const fetchedByType = await Promise.all(
          enabledRightsTypes.map(rightsType =>
            dispatch(
              rightsSplitsApi.endpoints.getTrackRightsSplits.initiate({
                trackId: sourceTrackId as number,
                rightsType,
              }),
            )
              .unwrap()
              .then(chains => [rightsType, chains] as const),
          ),
        )
        const calls: Array<Promise<unknown>> = []

        const queueBulkSave = (
          rightsType: RightsType,
          chains: RightsSplitChain[],
        ) => {
          if (chains.length === 0) return
          calls.push(
            dispatch(
              rightsSplitsApi.endpoints.bulkSaveRightsSplits.initiate({
                data: {
                  track_ids: targetTrackIds as number[],
                  rights_type: rightsType,
                  overwrite: true,
                  chains: chains.map(toChainRequest),
                },
              }),
            ).unwrap(),
          )
        }

        for (const [rightsType, chains] of fetchedByType) {
          const selectedForType = chains.filter(c => selectedChainIds.has(c.id))
          queueBulkSave(rightsType, selectedForType)
        }

        await Promise.all(calls)
        // Invalidate rights cache
        await Promise.all(
          targetTrackIds.map((targetId: TrackId) =>
            dispatch(
              rightsSplitsApi.endpoints.getTrackRightsSplits.initiate(
                { trackId: targetId as number },
                { forceRefetch: true },
              ),
            ).unwrap(),
          ),
        )
        syncRightsSlice(targetTrackIds)
      } catch (error) {
        undoRtkqPatches(rightsPatches)
        syncRightsSlice(rightsPatchedTargetIds)
        pushUINotification({
          dismissible: 'none',
          level: 'error',
          message:
            'Track info was copied, but rights failed to copy. Please try again.',
        })
        captureError(error, { extra: { level: 'info' } })
      }
    }

    const timeout: ReturnType<typeof setTimeout> = setTimeout(async () => {
      try {
        await api.bulkUpdateTrackMetadata(sourceTrackId, targetTrackIds, update)
        await copyRightsSplits()
        await copyNotes()
      } catch (error) {
        handleError(error)
      }
    }, REQUEST_DELAY * 1000)
    dispatch({
      type: TRACKS_UPDATE,
      tracks: futureState,
    })

    const undo = () => {
      clearTimeout(timeout)
      dispatch({
        type: TRACKS_UPDATE,
        tracks: previousState,
      })
      undoRtkqPatches(rightsPatches)
      syncRightsSlice(rightsPatchedTargetIds)
      undoRtkqPatches(discussionPatches)
    }

    pushUINotification({
      autoDismiss: REQUEST_DELAY,
      level: 'success',
      title: 'Track info copied!',
      message: `Track info was copied to ${plural(
        targetTrackIds.length,
        'track',
        'tracks',
      )}.`,
      action: {
        label: 'Undo',
        callback: undo,
      },
    })
  }
}
